import { Alert, Button, Card, Col, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from '../../pages/SampleDashboard.module.css';
import { getVisibleSampleManagerNavItems, SAMPLE_MANAGER_NAV_ITEMS } from './SampleManagerNavigation';
import { SamplePanelCardHeader } from './SamplePanelCardHeader';
import { SAMPLE_STORAGE_SETUP_HREF } from '../../utils/sampleStorageSetupGate';

export interface SampleDashboardQuickAccessProps {
  hasStorageRooms: boolean;
  hasSamples: boolean;
}

type QuickMeta = {
  path: string;
  enabled: boolean;
  icon: string;
  desc: string;
  disabledTitle?: string;
  /** ナビ path とリンク先が違うとき（setup=1 など） */
  href?: string;
};

/** ダッシュボードのクイックアクセス（ナビ定義を単一の源に。タイル見た目は統一） */
export default function SampleDashboardQuickAccess({
  hasStorageRooms,
  hasSamples,
}: SampleDashboardQuickAccessProps) {
  const { t } = useTranslation();
  const sampleOk = hasStorageRooms;
  const reportsOk = hasStorageRooms && hasSamples;
  const needsStorageTitle = t('dashboard.sample_requires_storage_room', {
    defaultValue: 'Register storage rooms before using sample management',
  });
  const needsSamplesTitle = t('dashboard.reports_requires_samples', {
    defaultValue: 'Register storage rooms and samples before using reports',
  });

  const metaByPath: QuickMeta[] = [
    {
      path: '/samples',
      enabled: sampleOk,
      icon: 'fa-flask',
      desc: t('sample.hub_cards.by_name_desc', {
        defaultValue: 'Browse and edit samples by name',
      }),
      disabledTitle: needsStorageTitle,
    },
    {
      path: '/samples/cases',
      enabled: sampleOk,
      icon: 'fa-th-large',
      desc: t('sample.hub_cards.by_case_desc', {
        defaultValue: 'Browse samples by storage case',
      }),
      disabledTitle: needsStorageTitle,
    },
    {
      path: '/storage-rooms',
      href: SAMPLE_STORAGE_SETUP_HREF,
      enabled: true,
      icon: 'fa-warehouse',
      desc: t('dashboard.storage_room_management_desc', {
        defaultValue: 'Manage storage rooms',
      }),
    },
    {
      path: '/reports',
      enabled: reportsOk,
      icon: 'fa-chart-bar',
      desc: reportsOk
        ? t('dashboard.reports_desc', {
            defaultValue: 'As-of-date totals and Excel export',
          })
        : t('dashboard.reports_requires_samples', {
            defaultValue: 'Storage rooms and samples must be registered',
          }),
      disabledTitle: needsSamplesTitle,
    },
    {
      path: '/samples/usage-records',
      enabled: reportsOk,
      icon: 'fa-history',
      desc: reportsOk
        ? t('dashboard.usage_records_desc', { defaultValue: 'View usage records by date' })
        : t('dashboard.reports_requires_samples', {
            defaultValue: 'Storage rooms and samples must be registered',
          }),
      disabledTitle: needsSamplesTitle,
    },
    {
      path: '/excel-import',
      enabled: sampleOk,
      icon: 'fa-file-excel',
      desc: sampleOk
        ? t('dashboard.excel_import_desc', { defaultValue: 'Import and export Excel files' })
        : t('dashboard.excel_import_requires_storage_room', {
            defaultValue: 'Storage room registration required',
          }),
      disabledTitle: t('dashboard.excel_import_requires_storage_room', {
        defaultValue: 'Register storage rooms before import/export',
      }),
    },
  ];

  const visiblePaths = new Set(getVisibleSampleManagerNavItems().map((n) => n.path));
  const items = metaByPath
    .filter((meta) => visiblePaths.has(meta.path as (typeof SAMPLE_MANAGER_NAV_ITEMS)[number]['path']))
    .map((meta) => {
      const nav = SAMPLE_MANAGER_NAV_ITEMS.find((n) => n.path === meta.path)!;
      const full = t(nav.labelKey, { defaultValue: nav.defaultLabel });
      const short = t(nav.shortLabelKey, { defaultValue: nav.defaultShortLabel });
      return { ...meta, full, short };
    });

  const quickTitle = t('dashboard.quick_access', { defaultValue: 'Quick access' });
  const setupCta = t('sample.list_empty_go_to_storage', {
    defaultValue: '保管庫を登録する',
  });
  const tileClass = `w-100 text-start ${styles.quickTile} ${styles.quickPrimary}`;
  /**
   * 保管庫あり: ヘッダのセクション select / md タブで足りるので出さない（二重ナビ）。
   * 保管庫なし（初期）: セットアップ案内＋タイルのみ表示。
   */
  if (hasStorageRooms) return null;

  return (
    <Card className="border-0 shadow-sm sample-panel-card mb-3">
      <SamplePanelCardHeader icon="fa-compass" title={quickTitle} />
      <Card.Body className="p-3">
        <Alert variant="info" className={`mb-3 ${styles.dependencyAlert}`}>
          <Alert.Heading className={styles.dependencyHeading}>
            {t('storage_room.setup_banner_title', {
              defaultValue: t('dashboard.dependency_info_title', {
                defaultValue: 'How to get started',
              }),
            })}
          </Alert.Heading>
          <p className="mb-2 small">
            {t('storage_room.setup_banner_desc', {
              defaultValue: t('dashboard.dependency_info_desc', {
                defaultValue: 'Register data in this order to use the sample management system:',
              }),
            })}
          </p>
          <ol className="mb-3 small ps-3">
            <li>
              {t('storage_room.setup_step1', {
                defaultValue: t('dashboard.dependency_step1', {
                  defaultValue: 'First, register storage rooms',
                }),
              })}
            </li>
            <li>
              {t('storage_room.setup_step2', {
                defaultValue: t('dashboard.dependency_step2', {
                  defaultValue: 'Then register samples (a storage room is required)',
                }),
              })}
            </li>
          </ol>
          <Button as={Link as any} to={SAMPLE_STORAGE_SETUP_HREF} variant="primary" size="sm">
            <i className="fas fa-warehouse me-1" aria-hidden="true" />
            {setupCta}
          </Button>
        </Alert>

        <Row className="g-2">
          {items.map((item) => {
            /*
             * タイトルは視覚用のみ（リンクの aria-label が正本）。
             * short/full を AT に両方出すと「一覧サンプル一覧」になるため aria-hidden。
             */
            const tileBody = (
              <div className="d-flex align-items-center gap-2 w-100">
                <i className={`fas ${item.icon} ${styles.quickTileIcon}`} aria-hidden="true" />
                <div className="flex-grow-1 text-start min-w-0" aria-hidden="true">
                  <div className={styles.quickTileTitle}>
                    <span className="d-md-none">{item.short}</span>
                    <span className="d-none d-md-inline">{item.full}</span>
                  </div>
                  <small className={`d-none d-md-block text-muted mt-1 ${styles.quickTileDesc}`}>
                    {item.desc}
                  </small>
                </div>
              </div>
            );
            const to = item.href ?? item.path;
            return (
              <Col xs={6} md={6} lg={4} key={item.path}>
                {item.enabled ? (
                  <Link
                    to={to}
                    className={`btn btn-outline-primary ${tileClass}`}
                    aria-label={item.full}
                    title={item.full}
                  >
                    {tileBody}
                  </Link>
                ) : (
                  <Button
                    type="button"
                    variant="outline-primary"
                    className={tileClass}
                    disabled
                    title={item.disabledTitle}
                    aria-disabled="true"
                    aria-label={item.disabledTitle || item.full}
                  >
                    {tileBody}
                  </Button>
                )}
              </Col>
            );
          })}
        </Row>
      </Card.Body>
    </Card>
  );
}
